import React, { useMemo, useState } from 'react';
import { Phone, MessageCircle, Check, AlertCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { PriorityBadge, StageBadge } from './Badges';
import { formatDateTime, todayEndISO, todayStartISO, weekEndISO } from '@/lib/format';

export default function FollowUpView({ contacts, onChange }) {
    const { profile } = useAuth();
    const navigate = useNavigate();
    const now = new Date();
    const todayEnd = new Date(todayEndISO());
    const todayStart = new Date(todayStartISO());
    const weekEnd = new Date(weekEndISO());

    const sections = useMemo(() => {
        const overdue = [], today = [], week = [];
        for (const c of contacts) {
            if (!c.next_followup_at) continue;
            const d = new Date(c.next_followup_at);
            if (d < todayStart) overdue.push(c);
            else if (d <= todayEnd) today.push(c);
            else if (d <= weekEnd) week.push(c);
        }
        const sortFn = (a, b) => new Date(a.next_followup_at) - new Date(b.next_followup_at);
        return { overdue: overdue.sort(sortFn), today: today.sort(sortFn), week: week.sort(sortFn) };
    }, [contacts]);

    const logActivity = async (c, type, desc) => {
        await supabase.from('pipeline_activities').insert({
            contact_id: c.id, organization_id: c.organization_id, agent_id: profile.id, activity_type: type, description: desc,
        });
    };

    const markDone = async (c) => {
        const next = new Date(); next.setDate(next.getDate() + 7); next.setHours(9, 0, 0, 0);
        await supabase.from('pipeline_contacts').update({
            last_contacted_at: new Date().toISOString(),
            next_followup_at: next.toISOString(),
        }).eq('id', c.id);
        await logActivity(c, 'note', 'Follow-up marked done');
        toast.success('Follow-up marked done');
        onChange?.();
    };

    const onCall = async (c) => {
        if (c.phone) window.open(`tel:${c.phone}`);
        await logActivity(c, 'call', 'Called from follow-up view');
        toast.success('Call logged');
        onChange?.();
    };
    const onWhatsApp = (c) => {
        const phone = (c.whatsapp || c.phone || '').replace(/[^0-9]/g, '');
        if (phone) Object.assign(document.createElement('a'), { href: `https://wa.me/${phone}`, target: '_blank', rel: 'noopener noreferrer' }).click();
        logActivity(c, 'whatsapp', 'WhatsApp from follow-up view');
        onChange?.();
    };

    return (
        <div className="px-4 lg:px-6 pb-6 space-y-6">
            <Section title="Overdue" icon={<AlertCircle className="h-4 w-4" />} color="#EF4444" rows={sections.overdue} onCall={onCall} onWhatsApp={onWhatsApp} markDone={markDone} navigate={navigate} />
            <Section title="Due today" icon={<Calendar className="h-4 w-4" />} color="#F59E0B" rows={sections.today} onCall={onCall} onWhatsApp={onWhatsApp} markDone={markDone} navigate={navigate} />
            <Section title="This week" icon={<Calendar className="h-4 w-4" />} color="#3B82F6" rows={sections.week} onCall={onCall} onWhatsApp={onWhatsApp} markDone={markDone} navigate={navigate} />
        </div>
    );
}

function Section({ title, icon, color, rows, onCall, onWhatsApp, markDone, navigate }) {
    return (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`fu-section-${title}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100" style={{ background: `${color}10` }}>
                <div className="flex items-center gap-2" style={{ color }}>
                    {icon}
                    <span className="font-semibold text-[14px]">{title}</span>
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-white border border-gray-200 text-gray-700">{rows.length}</span>
                </div>
            </div>
            <div>
                {rows.length === 0 ? (
                    <div className="py-8 text-center text-[12px] text-gray-400">Nothing in this window</div>
                ) : rows.map((c) => (
                    <div key={c.id} className="flex items-center gap-3 p-3 border-t border-gray-100 first:border-t-0">
                        <div onClick={() => navigate(`/pipeline/${c.id}`)} className="flex-1 min-w-0 cursor-pointer">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold text-aks-text truncate">{c.full_name}</span>
                                <PriorityBadge value={c.priority} />
                                <StageBadge value={c.pipeline_stage} />
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{c.phone || c.email} · {formatDateTime(c.next_followup_at)}</div>
                            {c.followup_notes && <div className="text-[12px] text-gray-600 mt-1 line-clamp-2">{c.followup_notes}</div>}
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => onCall(c)} className="h-9 w-9 rounded-md border border-gray-300 hover:border-aks-gold flex items-center justify-center text-aks-navy" title="Call" data-testid={`fu-call-${c.id}`}><Phone className="h-4 w-4" /></button>
                            <button onClick={() => onWhatsApp(c)} className="h-9 w-9 rounded-md border border-gray-300 hover:border-[#25D366] flex items-center justify-center text-[#25D366]" title="WhatsApp" data-testid={`fu-wa-${c.id}`}><MessageCircle className="h-4 w-4" /></button>
                            <button onClick={() => markDone(c)} className="h-9 px-3 rounded-md bg-aks-success text-white text-[12px] font-semibold flex items-center gap-1.5" data-testid={`fu-done-${c.id}`}><Check className="h-4 w-4" /> Done</button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
